import axios from 'axios';
import { Pool, Block, Miner, MinerStats, MinerSettings } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://kaspapool.cryptoware.uk/api';
const API_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT || 20000);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
});

const normalizeMinerAddress = (address: string) => {
  return encodeURIComponent(address.trim());
};

export const apiService = {
  // Pool endpoints
  async getPools(): Promise<Pool[]> {
    try {
      const response = await api.get('/pools');
      console.log('API Response:', response.data);
      
      // Handle the specific structure of your API
      let pools = response.data;
      
      // Your API returns { "pools": [...] }
      if (pools && pools.pools && Array.isArray(pools.pools)) {
        pools = pools.pools;
      }
      
      // Ensure we have an array
      if (!Array.isArray(pools)) {
        console.warn('Pools data is not an array:', pools);
        return [];
      }
      
      // Map your API structure to our Pool interface
      return pools.map((pool: any) => {
        // Convert ports object to array
        const portsArray = pool.ports ? Object.entries(pool.ports).map(([port, config]: [string, any]) => ({
          port: parseInt(port),
          difficulty: config.difficulty?.toString() || '1K',
          description: config.name || 'Default'
        })) : [];

        // Generate stratum URLs from ports
        const stratumUrls = portsArray.map(port => 
          `stratum+tcp://kaspapool.cryptoware.uk:${port.port}`
        );

        // Normalize algorithm names
        const normalizeAlgorithm = (algo: string) => {
          switch (algo?.toUpperCase()) {
            case 'SHA256D': return 'SHA256';
            case 'SCRYPT': return 'Scrypt';
            case 'ETHASH': return 'Ethash';
            case 'RANDOMX': return 'RandomX';
            case 'EQUIHASH': return 'Equihash';
            default: return algo || 'Unknown';
          }
        };

        const totalBlocks =
          pool.totalBlocks ??
          pool.poolStats?.totalBlocks ??
          pool.poolStats?.totalBlocksFound ??
          0;

        const blockTime =
          Number(
            pool.blockRefreshInterval ||
            pool.coin?.blockTime ||
            pool.coin?.targetBlockTime ||
            pool.networkStats?.blockTime ||
            pool.coin?.blockTimeSeconds ||
            1
          );

        return {
          id: pool.id,
          coin: pool.coin?.name || pool.coin?.symbol || 'Unknown',
          coinSymbol: pool.coin?.symbol || pool.coin?.name || pool.id || 'COIN',
          totalBlocks,
          algorithm: normalizeAlgorithm(pool.coin?.algorithm),
          hashrate: pool.poolStats?.poolHashrate || 0,
          minerCount: pool.poolStats?.connectedMiners || 0,
          fee: pool.poolFeePercent || 0,
          lastBlock: undefined, // Will need to get from blocks endpoint
          paymentType: pool.paymentProcessing?.payoutScheme || 'SOLO',
          minimumPayment: pool.paymentProcessing?.minimumPayment,
          ports: portsArray,
          stratumUrls: stratumUrls,
          stats: {
            networkhashrate: pool.networkStats?.networkHashrate || 0,
            networkdifficulty: pool.networkStats?.networkDifficulty || 0,
            blockReward: pool.blockReward || 0,
            blockTime,
            lastblocktime: pool.networkStats?.lastNetworkBlockTime ? 
              new Date(pool.networkStats.lastNetworkBlockTime).getTime() / 1000 : 0
          }
        };
      });
      
    } catch (error: any) {
      console.error('Error fetching pools:', error);
      // Return empty array if API fails
      return [];
    }
  },

  async getPool(poolId: string): Promise<Pool> {
    try {
      const response = await api.get(`/pools/${poolId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching pool ${poolId}:`, error);
      throw error;
    }
  },

  async getPoolBlocks(poolId: string, limit = 25): Promise<Block[]> {
    try {
      const response = await api.get(`/pools/${poolId}/blocks?limit=${limit}`);
      let blocks = response.data;
      
      // Handle different response formats
      if (blocks && !Array.isArray(blocks) && blocks.data) {
        blocks = blocks.data;
      }
      
      return Array.isArray(blocks) ? blocks : [];
    } catch (error) {
      console.error(`Error fetching blocks for pool ${poolId}:`, error);
      return [];
    }
  },

  async getPoolMiners(poolId: string, limit = 25): Promise<Miner[]> {
    try {
      const response = await api.get(`/pools/${poolId}/miners?limit=${limit}`);
      let miners = response.data;
      
      // Handle different response formats
      if (miners && !Array.isArray(miners) && miners.data) {
        miners = miners.data;
      }
      
      return Array.isArray(miners) ? miners : [];
    } catch (error) {
      console.error(`Error fetching miners for pool ${poolId}:`, error);
      return [];
    }
  },

  // Global endpoints
  async getAllBlocks(limit = 25): Promise<Block[]> {
    try {
      const response = await api.get(`/blocks?limit=${limit}`);
      let blocks = response.data;
      
      // Handle different response formats
      if (blocks && !Array.isArray(blocks) && blocks.data) {
        blocks = blocks.data;
      }
      
      return Array.isArray(blocks) ? blocks : [];
    } catch (error) {
      console.error('Error fetching all blocks:', error);
      return [];
    }
  },

  // Miner endpoints
  async getMinerStats(poolId: string, address: string): Promise<MinerStats> {
    const encodedAddress = normalizeMinerAddress(address);
    const response = await api.get<MinerStats>(`/pools/${poolId}/miners/${encodedAddress}`);
    return response.data;
  },

  async getMinerPayments(poolId: string, address: string, limit = 25): Promise<any[]> {
    const encodedAddress = normalizeMinerAddress(address);
    const response = await api.get(`/pools/${poolId}/miners/${encodedAddress}/payments?limit=${limit}`);
    return response.data;
  },

  async getMinerBlocks(poolId: string, address: string, limit = 25): Promise<Block[]> {
    const encodedAddress = normalizeMinerAddress(address);
    const response = await api.get<Block[]>(`/pools/${poolId}/miners/${encodedAddress}/blocks?limit=${limit}`);
    return response.data;
  },

  async getMinerPerformance(poolId: string, address: string, days = 7): Promise<any[]> {
    const encodedAddress = normalizeMinerAddress(address);
    const response = await api.get(`/pools/${poolId}/miners/${encodedAddress}/performance?days=${days}`);
    return response.data;
  },

  async getMinerSettings(poolId: string, address: string): Promise<MinerSettings | null> {
    const encodedAddress = normalizeMinerAddress(address);
    try {
      const response = await api.get(`/pools/${poolId}/miners/${encodedAddress}/settings`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching settings for miner ${address}:`, error);
      return null;
    }
  },

  async updateMinerSettings(
    poolId: string,
    address: string,
    paymentThreshold: number,
    ipAddress: string
  ): Promise<any> {
    const encodedAddress = normalizeMinerAddress(address);
    const payload = {
      settings: {
        PaymentThreshold: paymentThreshold
      },
      ipAddress
    };

    const response = await api.post(`/pools/${poolId}/miners/${encodedAddress}/settings`, payload);
    return response.data;
  },

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response = await api.get('/health-check');
    return response.data;
  }
};

// Format helpers
export const formatters = {
  hashrate: (hashrate: number | undefined | null): string => {
    if (!hashrate || hashrate === 0) return '0 H/s';
    
    const units = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s', 'EH/s'];
    let value = hashrate;
    let unitIndex = 0;
    
    while (value >= 1000 && unitIndex < units.length - 1) {
      value /= 1000;
      unitIndex++;
    }
    
    return `${value.toFixed(value >= 100 ? 0 : 2)} ${units[unitIndex]}`;
  },

  balance: (balance: number | undefined | null, decimals = 8): string => {
    return (balance || 0).toFixed(decimals);
  },

  percentage: (value: number | undefined | null): string => {
    return `${(value || 0).toFixed(2)}%`;
  },

  timeAgo: (timestamp: string | number | undefined | null): string => {
    if (timestamp === undefined || timestamp === null || timestamp === '') return 'Never';
    
    const now = Date.now();
    let raw: number;
    if (typeof timestamp === 'string') {
      const parsed = Date.parse(timestamp);
      if (Number.isNaN(parsed)) return 'Unknown';
      raw = parsed;
    } else {
      raw = Number(timestamp) * 1000;
      if (!Number.isFinite(raw)) return 'Unknown';
    }
    const diff = now - raw;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return `${seconds} second${seconds > 1 ? 's' : ''} ago`;
  },

  formatDate: (timestamp: string | number | undefined | null): string => {
    if (timestamp === undefined || timestamp === null || timestamp === '') return 'N/A';
    
    let date: Date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return 'N/A';
    } else {
      const ms = Number(timestamp) * 1000;
      if (!Number.isFinite(ms)) return 'N/A';
      date = new Date(ms);
    }
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }
};
